import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import type { ShopMedia, ShopMediaRef } from '@/lib/api/shop-media-api'
import type { ShopMediaForm } from '@/lib/schemas/shop-media-schemas'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet'
import {
    useDeleteShopMedia,
    useShopMediaUsage,
    useUpdateShopMedia,
} from '@/hooks/use-shop-media-query'
import { ApiError } from '@/lib/api/client'
import { formatBytes } from '@/lib/format'
import { isVideo } from '@/lib/api/shop-media-api'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'
import { createShopMediaSchema } from '@/lib/schemas/shop-media-schemas'
import { setShopServerErrors } from '@/components/retail/shop-errors'

/** Nơi dùng ảnh đọc được: "Nhóm sản phẩm: Chè Tôm Nõn · Ảnh bìa". */
export function mediaRefLabel(r: ShopMediaRef): string {
    const owner =
        {
            category: m.media_owner_category,
            group: m.media_owner_group,
            home: m.media_owner_home,
            post: m.media_owner_post,
            page: m.media_owner_page,
            setting: m.media_owner_setting,
            guide_video: m.media_owner_guide_video,
        }[r.owner_type] ?? m.media_owner_other
    const field = MEDIA_FIELD_LABELS[r.field]
    return [owner(), r.label ?? r.owner_id, field ? field() : r.field].join(
        ' · ',
    )
}

/** Tên ô chứa ảnh (shop_media_refs.field) đọc được; ô lạ giữ nguyên tên. */
const MEDIA_FIELD_LABELS: Record<string, () => string> = {
    image_url: m.media_field_image,
    cover_image_url: m.media_field_cover,
    cover: m.media_field_cover,
    seo_image_url: m.media_field_seo,
    og_image: m.media_field_seo,
    ocop: m.media_field_ocop,
    images: m.media_field_images,
    description_html: m.media_field_description,
    brew_html: m.media_field_brew,
    content_html: m.media_field_content,
    data: m.media_field_block,
    items: m.media_field_items,
    video: m.media_field_video,
    poster: m.media_field_poster,
}

// Giữ export cũ cho các nơi đang import từ đây; nguồn thật ở lib/format.
export { formatBytes }

/** Chi tiết một file: xem trước, sửa alt / tiêu đề, nơi đang dùng, xoá. */
export function MediaDetailSheet({
    item,
    onClose,
    canWrite,
}: {
    item: ShopMedia | null
    onClose: () => void
    canWrite: boolean
}) {
    return (
        <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full overflow-y-auto sm:max-w-md">
                {item && (
                    <MediaDetail
                        key={item.id}
                        item={item}
                        onClose={onClose}
                        canWrite={canWrite}
                    />
                )}
            </SheetContent>
        </Sheet>
    )
}

function MediaDetail({
    item,
    onClose,
    canWrite,
}: {
    item: ShopMedia
    onClose: () => void
    canWrite: boolean
}) {
    const usage = useShopMediaUsage(item.id)
    const update = useUpdateShopMedia()
    const remove = useDeleteShopMedia()
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const form = useForm<ShopMediaForm>({
        resolver: zodResolver(createShopMediaSchema()),
        defaultValues: { alt: item.alt, title: item.title ?? '' },
    })
    const { reset } = form
    useEffect(() => {
        reset({ alt: item.alt, title: item.title ?? '' })
    }, [item.alt, item.title, reset])

    const save = form.handleSubmit(async (v) => {
        try {
            await update.mutateAsync({
                id: item.id,
                input: { alt: v.alt, title: v.title.trim() || null },
            })
            toast.success(m.media_saved())
        } catch (err) {
            setShopServerErrors(form.setError, err, m.media_save_failed())
        }
    })

    const del = async () => {
        if (!window.confirm(m.media_delete_confirm({ name: item.filename })))
            return
        setDeleteError(null)
        try {
            await remove.mutateAsync(item.id)
            toast.success(m.media_deleted())
            onClose()
        } catch (err) {
            setDeleteError(
                (err instanceof ApiError &&
                    resolveApiErrorMessage(err.code, err.params)) ||
                    m.media_delete_failed(),
            )
            void usage.refetch()
        }
    }

    const refs = usage.data?.data ?? []
    return (
        <div className="space-y-5">
            <SheetHeader>
                <SheetTitle className="break-all">{item.filename}</SheetTitle>
                <SheetDescription>
                    {[
                        item.mime,
                        formatBytes(item.size),
                        item.width && item.height
                            ? m.media_dimensions({
                                  w: item.width,
                                  h: item.height,
                              })
                            : null,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </SheetDescription>
            </SheetHeader>
            <div className="overflow-hidden rounded-md border bg-muted">
                {isVideo(item) ? (
                    <video
                        src={item.url}
                        controls
                        preload="metadata"
                        className="w-full"
                    />
                ) : (
                    <img
                        src={item.url}
                        alt={item.alt}
                        className="max-h-72 w-full object-contain"
                    />
                )}
            </div>
            <FormProvider {...form}>
                <form noValidate onSubmit={save} className="space-y-3">
                    <Field name="alt" label={m.media_alt_label()}>
                        <Input
                            id="alt"
                            {...form.register('alt')}
                            readOnly={!canWrite}
                        />
                    </Field>
                    <p className="text-xs text-muted-foreground">
                        {m.media_alt_hint()}
                    </p>
                    <Field name="title" label={m.media_title_label()}>
                        <Input
                            id="title"
                            {...form.register('title')}
                            readOnly={!canWrite}
                        />
                    </Field>
                    {form.formState.errors.root?.server && (
                        <p className="text-sm text-destructive" role="alert">
                            {form.formState.errors.root.server.message}
                        </p>
                    )}
                    {canWrite && (
                        <Button type="submit" disabled={update.isPending}>
                            {update.isPending && (
                                <Loader2 className="size-4 animate-spin" />
                            )}
                            {m.media_save()}
                        </Button>
                    )}
                </form>
            </FormProvider>
            <section className="space-y-2">
                <h3 className="text-sm font-medium">{m.media_usage_title()}</h3>
                {usage.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                ) : refs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        {m.media_usage_none()}
                    </p>
                ) : (
                    <ul className="space-y-1 text-sm">
                        {refs.map((r) => (
                            <li
                                key={`${r.owner_type}-${r.owner_id}-${r.field}`}
                            >
                                {mediaRefLabel(r)}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
            {canWrite && (
                <div className="space-y-2 border-t pt-4">
                    <Button
                        type="button"
                        variant="outline"
                        className="text-destructive"
                        disabled={remove.isPending}
                        onClick={() => void del()}
                    >
                        <Trash2 className="size-4" />
                        {m.media_delete()}
                    </Button>
                    {deleteError && (
                        <p className="text-sm text-destructive" role="alert">
                            {deleteError}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}
